import * as React from "react"
import { Link as RRLink, NavLink as RRNavLink, useParams } from "react-router"
import { Link as UILink, type LinkProps as UILinkProps } from "~/components/ui/link"

// Docs live under a secret path prefix: `/<docsKey>/docs/...`. Every
// docs-internal link must carry that prefix so an agent (or a human) stays
// inside the gated subtree as it navigates. Rather than prefix ~200 link
// sites by hand, docs route files import these drop-in wrappers (aliased to
// the original component names), so the prefixing happens transparently.
//
// The secret is read from the current URL (`useParams().docsKey`) — never
// baked into the bundle. Only absolute docs paths (`/docs...`) are prefixed;
// external links, mailto:, hashes-on-other-paths, and already-prefixed or
// relative paths pass through untouched.

/** Hook returning a prefixer for docs-internal absolute paths. */
export function useDocsHref(): (to: string) => string {
  const { docsKey } = useParams()
  return React.useCallback(
    (to: string) => {
      if (!docsKey) return to
      if (
        to === "/docs" ||
        to.startsWith("/docs/") ||
        to.startsWith("/docs#") ||
        to === "/llms.txt" ||
        to === "/llms-full.txt"
      ) {
        return `/${docsKey}${to}`
      }
      return to
    },
    [docsKey],
  )
}

const prefixMaybe = (
  to: string | object,
  prefix: (t: string) => string,
): string | object => (typeof to === "string" ? prefix(to) : to)

/** Drop-in for `~/components/ui/link` Link — prefixes docs paths. */
export const DocsUiLink = React.forwardRef<HTMLAnchorElement, UILinkProps>(
  ({ to, ...rest }, ref) => {
    const prefix = useDocsHref()
    return <UILink ref={ref} to={prefix(to)} {...rest} />
  },
)
DocsUiLink.displayName = "DocsUiLink"

/** Drop-in for react-router Link — prefixes docs paths. */
export const DocsRouterLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof RRLink>
>(({ to, ...rest }, ref) => {
  const prefix = useDocsHref()
  return <RRLink ref={ref} to={prefixMaybe(to as string | object, prefix)} {...rest} />
})
DocsRouterLink.displayName = "DocsRouterLink"

/** Drop-in for react-router NavLink — prefixes docs paths. */
export const DocsNavLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof RRNavLink>
>(({ to, ...rest }, ref) => {
  const prefix = useDocsHref()
  return (
    <RRNavLink ref={ref} to={prefixMaybe(to as string | object, prefix)} {...rest} />
  )
})
DocsNavLink.displayName = "DocsNavLink"
